import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useCQuery, invalidateQuery, FC } from "../../Services";
import { DataGrid } from "@mui/x-data-grid";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import HistoryIcon from "@mui/icons-material/History";
import ComputerIcon from "@mui/icons-material/Computer";
import DnsIcon from "@mui/icons-material/Dns";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const stringify = (val) => {
  if (typeof val === "string") return val;
  if (val == null) return "";
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
};

const formatTimestamp = (value) => {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  if (dt.isValid) return dt.toFormat("dd/MM/yyyy HH:mm:ss");
  const dt2 = DateTime.fromJSDate(new Date(value));
  if (dt2.isValid) return dt2.toFormat("dd/MM/yyyy HH:mm:ss");
  return String(value);
};

const toQueryDate = (value) => {
  if (value == null) return null;
  if (typeof value === "string") return value;
  const dt = value?.isValid !== undefined ? value : DateTime.fromJSDate(value);
  return dt?.isValid ? dt.toISO() : null;
};

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const SOURCE_COLORS = {
  client: { bg: "#e3f2fd", color: "#1565c0", label: "Client" },
  server: { bg: "#f3e5f5", color: "#7b1fa2", label: "Server" },
  unknown: { bg: "#f5f5f5", color: "#757575", label: "—" },
};

const SourceChip = ({ value }) => {
  const cfg = SOURCE_COLORS[value] || SOURCE_COLORS.unknown;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 11,
        height: 22,
      }}
    />
  );
};

const LevelChip = ({ value }) => {
  const colors = {
    info: { bg: "#e8f5e9", color: "#2e7d32" },
    warn: { bg: "#fff3e0", color: "#e65100" },
    error: { bg: "#ffebee", color: "#c62828" },
  };
  const cfg = colors[value] || colors.info;
  return (
    <Chip
      label={value || "info"}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 11,
        height: 22,
        textTransform: "uppercase",
      }}
    />
  );
};

export const RestoredLogsPanel = () => {
  const [searchInput, setSearchInput] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [batchFilter, setBatchFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [levelFilter, setLevelFilter] = useState(null);
  const [clientFilter, setClientFilter] = useState(null);
  const [moduleFilter, setModuleFilter] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [selectedArchives, setSelectedArchives] = useState([]);
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteBatch, setDeleteBatch] = useState(null);
  const [deleteRunning, setDeleteRunning] = useState(false);

  const debouncedSearch = useDebounce(searchInput);

  const { data: clientsRaw = [] } = useCQuery("clients");
  const clientOptions = useMemo(() => {
    const list = Array.isArray(clientsRaw)
      ? clientsRaw
      : clientsRaw?.data || [];
    return list.map((c) => c._id).filter(Boolean).sort();
  }, [clientsRaw]);

  const { data: archivesRaw, isLoading: archivesLoading } =
    useCQuery("logArchives");
  const archives = useMemo(
    () =>
      Array.isArray(archivesRaw) ? archivesRaw : archivesRaw?.data || [],
    [archivesRaw]
  );

  const { data: batchesRaw, isLoading: batchesLoading } =
    useCQuery("restoreLogBatches");
  const batches = useMemo(
    () => (Array.isArray(batchesRaw) ? batchesRaw : []),
    [batchesRaw]
  );

  const restoredFilenames = useMemo(() => {
    const set = new Set();
    batches.forEach((b) =>
      (b.sourceArchives || []).forEach((f) => set.add(f))
    );
    return set;
  }, [batches]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    debouncedSearch,
    dateFrom,
    dateTo,
    batchFilter,
    sourceFilter,
    levelFilter,
    clientFilter,
    moduleFilter,
  ]);

  const queryParams = useMemo(() => {
    const params = {
      $limit: paginationModel.pageSize,
      $skip: paginationModel.page * paginationModel.pageSize,
      $sort: { serverTimestamp: -1 },
    };
    if (debouncedSearch) params.search = debouncedSearch;
    const from = toQueryDate(dateFrom);
    const to = toQueryDate(dateTo);
    if (from) params.dateFrom = from;
    if (to) params.dateTo = to;
    if (batchFilter) params.restoreBatchId = batchFilter;
    if (sourceFilter) params.source = sourceFilter;
    if (levelFilter) params.level = levelFilter;
    if (clientFilter) params.clientId = clientFilter;
    if (moduleFilter) params.module = moduleFilter;
    return params;
  }, [
    paginationModel,
    debouncedSearch,
    dateFrom,
    dateTo,
    batchFilter,
    sourceFilter,
    levelFilter,
    clientFilter,
    moduleFilter,
  ]);

  const handleClearFilters = useCallback(() => {
    setSearchInput("");
    setDateFrom(null);
    setDateTo(null);
    setBatchFilter(null);
    setSourceFilter(null);
    setLevelFilter(null);
    setClientFilter(null);
    setModuleFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const hasActiveFilters =
    searchInput ||
    dateFrom ||
    dateTo ||
    batchFilter ||
    sourceFilter ||
    levelFilter ||
    clientFilter ||
    moduleFilter;

  const { data: response, isLoading, isFetching } = useCQuery(
    ["restoredLogs", queryParams],
    [],
    { placeholderData: (prev) => prev }
  );

  const rows = useMemo(() => {
    const list = response?.data || [];
    return list.map((log, idx) => ({
      ...log,
      _uid: log._id || `restored-${idx}`,
    }));
  }, [response]);

  const rowCount = response?.total ?? rows.length;

  const batchOptions = useMemo(
    () =>
      batches.map((b) => ({
        id: b.restoreBatchId,
        label: `${formatTimestamp(b.restoreBatchAt)} — ${(b.sourceArchives || []).join(", ")} (${b.totalLogs?.toLocaleString("it-IT")} log)`,
      })),
    [batches]
  );

  const handleRestore = useCallback(async () => {
    if (selectedArchives.length === 0) {
      setSnackbar({
        open: true,
        message: "Seleziona almeno un archivio ZIP.",
        severity: "warning",
      });
      return;
    }
    setRestoreRunning(true);
    try {
      const result = await FC.restoreLogsFromArchives(selectedArchives);
      setSnackbar({
        open: true,
        message: `Ripristinati ${result.totalInserted?.toLocaleString("it-IT") || 0} log da ${selectedArchives.length} archivio/i.`,
        severity: "success",
      });
      setSelectedArchives([]);
      invalidateQuery("restoreLogBatches");
      invalidateQuery("restoredLogs");
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || "Errore durante il ripristino.",
        severity: "error",
      });
    } finally {
      setRestoreRunning(false);
    }
  }, [selectedArchives]);

  const handleDeleteBatch = useCallback(async () => {
    if (!deleteBatch?.restoreBatchId) return;
    setDeleteRunning(true);
    try {
      const res = await FC.service("restored-logs").remove(
        deleteBatch.restoreBatchId
      );
      setSnackbar({
        open: true,
        message: `Eliminati ${res.deletedCount?.toLocaleString("it-IT") || 0} log ripristinati.`,
        severity: "success",
      });
      setDeleteBatch(null);
      if (batchFilter === deleteBatch.restoreBatchId) setBatchFilter(null);
      invalidateQuery("restoreLogBatches");
      invalidateQuery("restoredLogs");
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || "Errore durante l'eliminazione.",
        severity: "error",
      });
    }
    setDeleteRunning(false);
  }, [deleteBatch, batchFilter]);

  const toggleArchive = (filename) => {
    setSelectedArchives((prev) =>
      prev.includes(filename)
        ? prev.filter((f) => f !== filename)
        : [...prev, filename]
    );
  };

  const columns = [
    {
      field: "serverTimestamp",
      headerName: "Data / Ora",
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
          {formatTimestamp(params.value)}
        </Typography>
      ),
    },
    {
      field: "source",
      headerName: "Sorgente",
      width: 100,
      sortable: false,
      renderCell: (params) => <SourceChip value={params.value} />,
    },
    {
      field: "level",
      headerName: "Livello",
      width: 90,
      sortable: false,
      renderCell: (params) => <LevelChip value={params.value} />,
    },
    {
      field: "message",
      headerName: "Messaggio",
      flex: 1,
      minWidth: 240,
      sortable: false,
      valueGetter: (value) => stringify(value),
    },
    {
      field: "restoreBatchId",
      headerName: "Batch",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <Typography variant="body2" noWrap sx={{ fontFamily: "monospace", fontSize: 11 }}>
            {params.value ? String(params.value).slice(0, 8) + "…" : "—"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "clientId",
      headerName: "Client",
      width: 120,
      sortable: false,
    },
  ];

  return (
    <Box sx={{ mx: "auto", width: "94%", maxWidth: 1600, pb: 5 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, mt: 1 }}>
        <HistoryIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700}>
          Log ripristinati
        </Typography>
        {!isLoading && (
          <Chip label={`${rowCount} totali`} size="small" variant="outlined" />
        )}
        {isFetching && !isLoading && <CircularProgress size={16} />}
      </Stack>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          Ripristina da archivi ZIP
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleziona uno o più archivi e ripristina i log nella collection separata{" "}
          <code>restored_logs</code> (non modifica i log live).
        </Typography>
        {archivesLoading ? (
          <CircularProgress size={24} />
        ) : archives.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nessun archivio disponibile. Esegui prima un backup dalla pagina Logs.
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 220, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>File</TableCell>
                    <TableCell align="right">Log</TableCell>
                    <TableCell>Data backup</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archives.map((a) => {
                    const alreadyRestored = restoredFilenames.has(a.filename);
                    return (
                    <TableRow
                      key={a.filename}
                      hover
                      sx={
                        alreadyRestored
                          ? { opacity: 0.65, backgroundColor: "action.selected" }
                          : undefined
                      }
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedArchives.includes(a.filename)}
                          disabled={alreadyRestored}
                          onChange={() => toggleArchive(a.filename)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {a.filename}
                        {alreadyRestored && (
                          <Chip
                            label="Già ripristinato"
                            size="small"
                            sx={{ ml: 1, fontSize: 10, height: 20 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">{a.totalLogs?.toLocaleString("it-IT")}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {formatTimestamp(a.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Button
              variant="contained"
              startIcon={
                restoreRunning ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <RestoreIcon />
                )
              }
              disabled={restoreRunning || selectedArchives.length === 0}
              onClick={handleRestore}
              sx={{ textTransform: "none" }}
            >
              {restoreRunning
                ? "Ripristino in corso…"
                : `Ripristina selezionati (${selectedArchives.length})`}
            </Button>
          </>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Batch di ripristino
          </Typography>
          <IconButton size="small" onClick={() => invalidateQuery("restoreLogBatches")}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
        {batchesLoading ? (
          <CircularProgress size={24} />
        ) : batches.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nessun batch. Ripristina almeno un archivio.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data ripristino</TableCell>
                  <TableCell>Archivi sorgente</TableCell>
                  <TableCell align="right">Log</TableCell>
                  <TableCell align="center">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((b) => (
                  <TableRow key={b.restoreBatchId} hover>
                    <TableCell sx={{ fontSize: 12 }}>
                      {formatTimestamp(b.restoreBatchAt)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                      {(b.sourceArchives || []).join(", ")}
                    </TableCell>
                    <TableCell align="right">{b.totalLogs?.toLocaleString("it-IT")}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Filtra tabella per questo batch">
                        <Button
                          size="small"
                          onClick={() => setBatchFilter(b.restoreBatchId)}
                          sx={{ textTransform: "none", mr: 0.5 }}
                        >
                          Filtra
                        </Button>
                      </Tooltip>
                      <Tooltip title="Elimina tutti i log di questo batch">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteBatch(b)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1.5, alignItems: "center" }}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            Filtri
          </Typography>
          <TextField
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cerca messaggio..."
            sx={{ minWidth: 200 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sourceFilter}
            onChange={(e, val) => setSourceFilter(val)}
            sx={{ height: 32 }}
          >
            <ToggleButton
              value="client"
              sx={{ textTransform: "none", px: 1.5, fontSize: 12 }}
            >
              <ComputerIcon sx={{ fontSize: 14, mr: 0.5 }} /> Client
            </ToggleButton>
            <ToggleButton
              value="server"
              sx={{ textTransform: "none", px: 1.5, fontSize: 12 }}
            >
              <DnsIcon sx={{ fontSize: 14, mr: 0.5 }} /> Server
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={levelFilter}
            onChange={(e, val) => setLevelFilter(val)}
            sx={{ height: 32 }}
          >
            <ToggleButton
              value="info"
              sx={{ textTransform: "none", px: 1.5, fontSize: 12, color: "#2e7d32" }}
            >
              info
            </ToggleButton>
            <ToggleButton
              value="warn"
              sx={{ textTransform: "none", px: 1.5, fontSize: 12, color: "#e65100" }}
            >
              warn
            </ToggleButton>
            <ToggleButton
              value="error"
              sx={{ textTransform: "none", px: 1.5, fontSize: 12, color: "#c62828" }}
            >
              error
            </ToggleButton>
          </ToggleButtonGroup>
          <Autocomplete
            size="small"
            options={clientOptions}
            value={clientFilter}
            onChange={(e, val) => setClientFilter(val)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Client..." sx={{ minWidth: 180 }} />
            )}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            value={moduleFilter || ""}
            onChange={(e) => setModuleFilter(e.target.value || null)}
            placeholder="Modulo..."
            sx={{ minWidth: 150 }}
          />
          <Autocomplete
            size="small"
            options={batchOptions}
            value={batchOptions.find((o) => o.id === batchFilter) || null}
            onChange={(_, val) => setBatchFilter(val?.id || null)}
            getOptionLabel={(o) => o.label}
            renderInput={(params) => (
              <TextField {...params} placeholder="Batch" sx={{ minWidth: 280 }} />
            )}
            sx={{ minWidth: 280 }}
          />
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          sx={{ gap: 1.5, alignItems: "center", mt: 1 }}
        >
          <DateTimePicker
            label="Da"
            value={dateFrom}
            onChange={setDateFrom}
            ampm={false}
            slotProps={{ textField: { size: "small", sx: { minWidth: 200 } } }}
          />
          <DateTimePicker
            label="A"
            value={dateTo}
            onChange={setDateTo}
            ampm={false}
            slotProps={{ textField: { size: "small", sx: { minWidth: 200 } } }}
          />
          {hasActiveFilters && (
            <Chip
              label="Cancella filtri"
              size="small"
              variant="outlined"
              onDelete={handleClearFilters}
              onClick={handleClearFilters}
            />
          )}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._uid}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          loading={isLoading}
          disableRowSelectionOnClick
          disableColumnMenu
          localeText={{ noRowsLabel: "Nessun log ripristinato." }}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            "& .MuiDataGrid-columnHeader": { color: "#fff", backgroundColor: "#004680" },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, color: "#fff" },
          }}
        />
      </Paper>

      <Dialog
        open={!!deleteBatch}
        onClose={() => !deleteRunning && setDeleteBatch(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Elimina batch ripristinato
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Confermi di voler eliminare questo batch di log ripristinati?
          </Typography>
          {deleteBatch?.sourceArchives?.length > 0 && (
            <Typography variant="body2" sx={{ mb: 1, fontFamily: "monospace" }}>
              {deleteBatch.sourceArchives.join(", ")}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Verranno eliminati <strong>{deleteBatch?.totalLogs?.toLocaleString("it-IT") || 0}</strong> log
            dalla collection <code>restored_logs</code>. L&apos;operazione non è reversibile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDeleteBatch(null)}
            disabled={deleteRunning}
            sx={{ textTransform: "none" }}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleDeleteBatch}
            disabled={deleteRunning}
            startIcon={deleteRunning ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ textTransform: "none" }}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
